<script setup>
import Sortable from 'sortablejs'
import { HaiType, haiTypeTitle, haiTypeColor } from '@/enums/quality/hai-type'
import HaiCriteriaService from '@/services/quality/hai-criteria.service'
import HaiCriteriaDrawer from '@/views/quality/infection-control/hai/HaiCriteriaDrawer.vue'
import { VDataTable } from "vuetify/labs/VDataTable"

definePage({
  meta: {
    subject: 'quality.infection_control.master',
    action: 'hai_criteria',
  },
})

const dataTable = ref()
const renderKey = ref(0)
const items = ref([])
const isLoading = ref(false)

// Filters
const selectedHaiType = ref(Object.values(HaiType)[0])
const selectedType = ref('criterion')

const haiTypeOptions = computed(() =>
  Object.values(HaiType).map(val => ({
    title: haiTypeTitle(val),
    value: val,
  })),
)

const typeOptions = [
  { title: 'Criterion', value: 'criterion' },
  { title: 'Exclusion', value: 'exclusion' },
]

const headers = [
  { title: '', key: 'drag', sortable: false, width: '40px' },
  { title: 'Title', key: 'title', sortable: false },
  { title: 'Type', key: 'type', sortable: false, width: '120px' },
  { title: 'HAI Type', key: 'hai_type', sortable: false, width: '120px' },
  { title: 'Status', key: 'status', sortable: false, width: '100px' },
  { title: 'Actions', key: 'actions', sortable: false, width: '100px' },
]

// Drawer
const isDrawerOpen = ref(false)
const drawerFormData = ref(null)

// Delete
const isDeleteDialogOpen = ref(false)

const fetchData = async () => {
  isLoading.value = true

  const params = {}

  if (selectedHaiType.value) params.hai_type = selectedHaiType.value
  if (selectedType.value) params.type = selectedType.value

  try {
    const data = await HaiCriteriaService.getAll(params)

    items.value = data ?? []
  } catch {
    items.value = []
  } finally {
    isLoading.value = false
  }
}

const forceRerender = () => {
  renderKey.value++
}

const initSortable = () => {
  nextTick(() => {
    const tableEl = dataTable.value?.$el
    if (!tableEl) return

    const tbody = tableEl.querySelector('tbody')
    if (!tbody) return

    Sortable.create(tbody, {
      handle: '.drag-handle',
      animation: 150,
      ghostClass: 'sortable-ghost',
      onEnd: async evt => {
        const movedItem = items.value.splice(evt.oldIndex, 1)[0]

        items.value.splice(evt.newIndex, 0, movedItem)

        const ids = items.value.map(item => item.id)

        await HaiCriteriaService.reorder(ids, () => {
          forceRerender()
        })
      },
    })
  })
}

watch([selectedHaiType, selectedType], () => {
  fetchData()
})

const openCreateDrawer = () => {
  drawerFormData.value = null
  isDrawerOpen.value = true
}

const openEditDrawer = item => {
  drawerFormData.value = { ...item }
  isDrawerOpen.value = true
}

const deleteTargetItem = ref(null)

const confirmDelete = item => {
  deleteTargetItem.value = item
  isDeleteDialogOpen.value = true
}

const handleDelete = async (confirm, data) => {
  if (!confirm || !data?.id) {
    deleteTargetItem.value = null

    return
  }

  await HaiCriteriaService.destroy(data.id, () => {
    isDeleteDialogOpen.value = false
    fetchData()
  })

  deleteTargetItem.value = null
}

onMounted(() => {
  fetchData()
  initSortable()
})

watch(renderKey, () => {
  initSortable()
})
</script>

<template>
  <div>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <AppSelect
                v-model="selectedHaiType"
                :items="haiTypeOptions"
                item-title="title"
                item-value="value"
                placeholder="Filter by HAI Type"
                clearable
                hide-details
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <AppSelect
                v-model="selectedType"
                :items="typeOptions"
                item-title="title"
                item-value="value"
                placeholder="Filter by Type"
                clearable
                hide-details
              />
            </VCol>
          </VRow>
        </template>
        <template #append>
          <VBtn
            variant="flat"
            prepend-icon="tabler-plus"
            @click="openCreateDrawer"
          >
            Add Criteria
          </VBtn>
        </template>
      </VCardItem>

      <VDivider />

      <!-- Table -->
      <VDataTable
        ref="dataTable"
        :key="renderKey"
        :headers="headers"
        :items="items"
        :loading="isLoading"
        :items-per-page="-1"
        hide-default-footer
      >
        <template #item.drag>
          <VIcon
            class="drag-handle cursor-grab"
            icon="tabler-arrows-maximize"
            size="20"
          />
        </template>

        <template #item.type="{ item }">
          <VChip
            :color="item.type === 'criterion' ? 'primary' : 'warning'"
            size="small"
            label
          >
            {{ item.type === 'criterion' ? 'Criterion' : 'Exclusion' }}
          </VChip>
        </template>

        <template #item.hai_type="{ item }">
          <VChip
            :color="haiTypeColor(item.hai_type)"
            size="small"
            label
            variant="tonal"
          >
            {{ haiTypeTitle(item.hai_type) }}
          </VChip>
        </template>

        <template #item.status="{ item }">
          <span :class="item.status ? 'text-success' : 'text-error'">
            {{ item.status ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1">
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              @click="openEditDrawer(item)"
            >
              <VIcon
                icon="tabler-pencil"
                size="16"
              />
            </IconBtn>
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              color="error"
              @click="confirmDelete(item)"
            >
              <VIcon
                icon="tabler-trash"
                size="16"
              />
            </IconBtn>
          </div>
        </template>

        <template #bottom />
      </VDataTable>
    </VCard>

    <!-- Drawer -->
    <HaiCriteriaDrawer
      v-model:is-drawer-open="isDrawerOpen"
      :form-data="drawerFormData"
      @refetch-listing="fetchData"
    />

    <!-- Delete Confirm -->
    <ConfirmDialog
      v-model:is-dialog-visible="isDeleteDialogOpen"
      confirmation-question="Are you sure you want to delete this criteria?"
      confirm-title="Deleted!"
      confirm-msg="Criteria deleted."
      :data="deleteTargetItem"
      @confirm="handleDelete"
    />
  </div>
</template>

<style>
.sortable-ghost {
  opacity: 0.5;
  background: rgba(var(--v-theme-primary), 0.08);
}
</style>
